import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../lib/supabase';

const AuthCallback = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const redirectTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        console.log("Auth callback handling initiated");
        
        const { data, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          throw sessionError;
        }
        
        if (data.session) {
          // Authentication successful
          setStatus('success');
          
          // Set timeout for redirection (5 seconds)
          redirectTimeout.current = setTimeout(() => {
            // For local development, just navigate to the app's dashboard
            const isLocalhost = window.location.hostname === 'localhost' || 
                               window.location.hostname === '127.0.0.1';
            
            if (isLocalhost) {
              navigate('/', { replace: true });
            } else {
              // Production environment logic
              const isAppSubdomain = window.location.hostname.startsWith('app.');
              
              if (isAppSubdomain) {
                navigate('/', { replace: true });
              } else {
                window.location.href = `https://app.${window.location.hostname.replace(/^www\./, '')}/`;
              }
            }
          }, 5000);
        } else {
          throw new Error('Authentication completed but no session was created');
        }
      } catch (err) {
        console.error('Error during authentication callback:', err);
        setError(err instanceof Error ? err.message : 'Authentication failed');
        setStatus('error');
      }
    };

    handleAuthCallback();

    // Cleanup function
    return () => {
      if (redirectTimeout.current) clearTimeout(redirectTimeout.current);
    };
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white via-[#fff5eb] to-[#f9f6f4] p-4 overflow-hidden">
      <div className="relative w-full max-w-md">
        {/* Background decorative elements */}
        <motion.div 
          className="absolute -z-10 top-40 -left-32 w-64 h-64 rounded-full bg-[#ff8c42]/10 blur-3xl"
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.5, 0.7, 0.5]
          }} 
          transition={{ 
            duration: 8, 
            repeat: Infinity,
            repeatType: "reverse"
          }}
        />
        
        <motion.div 
          className="absolute -z-10 -bottom-20 -right-16 w-72 h-72 rounded-full bg-[#946e5d]/10 blur-3xl"
          animate={{ 
            scale: [1, 1.3, 1],
            opacity: [0.5, 0.8, 0.5]
          }} 
          transition={{ 
            duration: 10, 
            repeat: Infinity,
            repeatType: "reverse",
            delay: 1
          }}
        />
        
        {/* Card with content */}
        <motion.div 
          className="bg-white rounded-2xl shadow-xl p-8 w-full relative z-10 overflow-hidden"
          initial={{ opacity: 0, y: 30, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ 
            duration: 0.6,
            type: "spring",
            bounce: 0.3
          }}
        >
          {/* Animated stripes in background */}
          <div className="absolute inset-0 -z-10 overflow-hidden opacity-30">
            <motion.div 
              className="absolute -inset-x-full top-0 h-1/3 bg-gradient-to-r from-transparent via-[#ff8c42]/20 to-transparent"
              animate={{ x: ['-100%', '300%'] }}
              transition={{ duration: 5, repeat: Infinity, ease: "linear" }}
            />
            <motion.div 
              className="absolute -inset-x-full top-1/3 h-1/3 bg-gradient-to-r from-transparent via-[#ff8c42]/10 to-transparent"
              animate={{ x: ['-100%', '300%'] }}
              transition={{ duration: 7, repeat: Infinity, ease: "linear", delay: 0.5 }}
            />
            <motion.div 
              className="absolute -inset-x-full top-2/3 h-1/3 bg-gradient-to-r from-transparent via-[#ff8c42]/15 to-transparent"
              animate={{ x: ['-100%', '300%'] }}
              transition={{ duration: 6, repeat: Infinity, ease: "linear", delay: 1 }}
            />
          </div>
          
          {/* Content */}
          <div className="relative z-10">
            <AnimatePresence mode="wait">
              {status === 'processing' && (
                <motion.div 
                  key="processing"
                  className="flex flex-col items-center text-center"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <motion.div 
                    className="w-24 h-24 mb-6 rounded-full bg-[#fff5eb] flex items-center justify-center"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  >
                    <motion.svg 
                      width="40" 
                      height="40" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      xmlns="http://www.w3.org/2000/svg" 
                      className="text-[#ff8c42]"
                    >
                      <motion.path 
                        d="M12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2Z" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeDasharray="60"
                        strokeDashoffset={150}
                        animate={{ strokeDashoffset: [150, 30] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                      />
                    </motion.svg>
                  </motion.div>
                  
                  <h2 className="text-2xl font-bold text-[#694e42] mb-3">
                    Verifying Your Identity
                  </h2>
                  
                  <p className="text-[#8a7266]">
                    Please wait while we complete your sign-in process...
                  </p>
                </motion.div>
              )}
              
              {status === 'success' && (
                <motion.div 
                  key="success"
                  className="flex flex-col items-center text-center"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <motion.div 
                    className="w-24 h-24 mb-6 rounded-full bg-green-50 flex items-center justify-center"
                    initial={{ scale: 0.5, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ 
                      type: "spring",
                      stiffness: 200,
                      damping: 10
                    }}
                  >
                    <motion.svg 
                      width="40" 
                      height="40" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      xmlns="http://www.w3.org/2000/svg" 
                      className="text-green-500"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ duration: 0.8, ease: "easeOut" }}
                    >
                      <motion.circle 
                        cx="12" 
                        cy="12" 
                        r="10" 
                        stroke="currentColor" 
                        strokeWidth="2"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 0.8 }}
                      />
                      <motion.path 
                        d="M8 12L11 15L16 9" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 0.6, delay: 0.4 }}
                      />
                    </motion.svg>
                  </motion.div>
                  
                  <motion.h2 
                    className="text-2xl font-bold text-[#694e42] mb-3"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                  >
                    Authentication Successful!
                  </motion.h2>
                  
                  <motion.p 
                    className="text-[#8a7266] mb-5"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                  >
                    You'll be redirected to your dashboard in a few seconds...
                  </motion.p>
                  
                  {/* Success pulse animation */}
                  <motion.div 
                    className="mt-5 flex justify-center space-x-2"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.7 }}
                  >
                    {[0, 1, 2].map((i) => (
                      <motion.div
                        key={i}
                        className="w-2 h-2 rounded-full bg-[#ff8c42]"
                        animate={{ 
                          scale: [1, 1.5, 1],
                          opacity: [0.5, 1, 0.5]
                        }}
                        transition={{ 
                          duration: 1.5, 
                          repeat: Infinity, 
                          delay: i * 0.3,
                          repeatType: "reverse"
                        }}
                      />
                    ))}
                  </motion.div>
                </motion.div>
              )}
              
              {status === 'error' && (
                <motion.div 
                  key="error"
                  className="flex flex-col items-center text-center"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <motion.div 
                    className="w-24 h-24 mb-6 rounded-full bg-red-50 flex items-center justify-center"
                    initial={{ scale: 0.5, opacity: 0 }}
                    animate={{ 
                      scale: 1, 
                      opacity: 1,
                      rotate: [0, 5, -5, 0]
                    }}
                    transition={{ 
                      type: "spring",
                      stiffness: 200,
                      damping: 10,
                      rotate: { 
                        duration: 0.5, 
                        delay: 0.2 
                      }
                    }}
                  >
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-red-500">
                      <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M15 9L9 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M9 9L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </motion.div>
                  
                  <motion.h2 
                    className="text-2xl font-bold text-[#694e42] mb-3"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    Authentication Failed
                  </motion.h2>
                  
                  <motion.p 
                    className="text-[#8a7266] mb-6"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                  >
                    {error || "We couldn't complete your sign-in. Please try again."}
                  </motion.p>
                  
                  <motion.button 
                    onClick={() => navigate('/connect')}
                    className="px-6 py-3 bg-gradient-to-r from-[#ff8c42] to-[#ff7a29] text-white rounded-xl font-medium shadow-md shadow-[#ff8c42]/20 hover:shadow-lg hover:shadow-[#ff8c42]/30 transition-all"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Return to Sign In
                  </motion.button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AuthCallback;